import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, MessageSquare, User, Bell, Shield, ClipboardList } from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { isAdminEmail } from '../lib/admins';

const MobileNav = () => {
  const [notifUnreadCount, setNotifUnreadCount] = React.useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = React.useState(0);
  const { user, userRole } = useAuth();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!user) return;
    
    // Notifications count
    const qNotifs = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false)
    );
    const unsubscribeNotifs = onSnapshot(qNotifs, (snapshot) => {
      setNotifUnreadCount(snapshot.size);
    }, (error) => {
      console.error("Error fetching unread notifications count in MobileNav:", error);
    });

    // Messages count
    const qMsgs = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );
    const unsubscribeMsgs = onSnapshot(qMsgs, (snapshot) => {
      let count = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const userUnread = data.unreadCounts?.[user.uid] || 0;
        count += userUnread;
      });
      setMessagesUnreadCount(count);
    }, (error) => {
      console.error("Error fetching unread messages count in MobileNav:", error);
    });

    return () => {
      unsubscribeNotifs();
      unsubscribeMsgs();
    };
  }, [user]);

  return (
    <nav className="fixed bottom-0 left-0 w-full md:hidden bg-black/80 backdrop-blur-lg border-t border-white/10 z-50 px-4 py-3">
      <div className="max-w-md mx-auto flex items-center justify-between">
        <NavLink
          to="/home"
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 transition-all duration-300 ${
              isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard 
                size={22} 
                className={isActive ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.7)]' : ''} 
              />
              <span className="text-[9px] font-bold uppercase tracking-[0.1em]">{t('mobileNav.dashboard')}</span>
            </>
          )}
        </NavLink>

        {userRole === 'stand' && (
          <NavLink
            to="/stand/festivals"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 transition-all duration-300 ${
                isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <ClipboardList
                  size={22}
                  className={isActive ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.7)]' : ''}
                />
                <span className="text-[9px] font-bold uppercase tracking-[0.1em]">{t('nav.myFestivals')}</span>
              </>
            )}
          </NavLink>
        )}

        <NavLink
          to="/messages"
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 transition-all duration-300 ${
              isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <MessageSquare 
                  size={22} 
                  className={isActive ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.7)]' : ''} 
                />
                {messagesUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#FF5C00] text-white text-[8px] font-black flex items-center justify-center rounded-full border border-black shadow-[0_0_10px_rgba(255,92,0,0.5)]">
                    {messagesUnreadCount > 9 ? '9+' : messagesUnreadCount}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.1em]">{t('mobileNav.chat')}</span>
            </>
          )}
        </NavLink>

        <NavLink
          to="/notifications"
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 transition-all duration-300 ${
              isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <Bell 
                  size={22} 
                  className={isActive ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.7)]' : ''} 
                />
                {notifUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#FF5C00] text-white text-[8px] font-black flex items-center justify-center rounded-full border border-black animate-pulse">
                    {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.1em]">{t('mobileNav.notifs')}</span>
            </>
          )}
        </NavLink>

        <NavLink
          to="/stand/profile"
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 transition-all duration-300 ${
              isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <User 
                  size={22} 
                  className={isActive ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.7)]' : ''} 
                />
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.1em]">{t('mobileNav.profile')}</span>
            </>
          )}
        </NavLink>

        {isAdminEmail(user?.email) && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 transition-all duration-300 ${
                isActive ? 'text-red-400' : 'text-zinc-500 hover:text-red-400/80'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <Shield 
                    size={22} 
                    className={isActive ? 'drop-shadow-[0_0_8px_rgba(248,113,113,0.7)]' : ''} 
                  />
                </div>
                <span className="text-[9px] font-bold uppercase tracking-[0.1em]">Admin</span>
              </>
            )}
          </NavLink>
        )}
      </div>
    </nav>
  );
};

export default MobileNav;
